import { isDefined } from "~/utils/isDefined";
import { normalizeCinemaCookiePath } from "~/utils/normalizeCinemaCookiePath";
import { COOKIES_NAMES } from "~/utils/constants.js";
import {
  DEFAULT_CINEMA_HANDLER,
  DEFAULT_CINEMA_LANGUAGE,
  isValidCinemaLang,
  isValidCinemaSlug,
} from "#build/imports";

// 1 year. Without an explicit maxAge this is a session cookie and the remembered
// cinema is lost when the browser closes (CIN-689).
//
// Host-only on purpose — no `domain`. COOKIES_DOMAIN exists solely to share the auth
// cookies (token, x-client, profileImage, ref) with websale.cinestar.cz as literally the
// same cookie, not a copy (composables/useAuthStorage.js). This cookie has no
// cross-subdomain consumer — it is main-site state only — so domain scoping was never
// applicable to it. It only ever carried one because the pre-CIN-611 useClientCookie
// applied COOKIES_DOMAIN to every cookie written through it (CIN-189, 2024-05-01);
// plugins/cleanupOldCookies.client.ts clears up that leftover.
//
// Safari/iOS caps script-written cookies at 7 days (ITP) regardless of maxAge, and
// useClientCookie deliberately never writes server-side (an SSR Set-Cookie would make
// the response uncacheable for Cloudflare). A year is therefore unreachable there;
// refreshExpiry below keeps sliding the 7-day window forward on every visit instead.
const LOCATION_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const LOCATION_COOKIE_OPTIONS = {
  path: "/",
  maxAge: LOCATION_COOKIE_MAX_AGE,
  sameSite: "lax" as const,
};

/**
 * How many cookies of this name the document carries. More than one means a legacy
 * domain-scoped cookie coexists with the host-only one — see refreshExpiry.
 */
const countRawCookies = (name: string) =>
  document.cookie
    .split(";")
    .filter((entry) => entry.split("=")[0].trim() === name).length;

/**
 * Render LOCATION_COOKIE_OPTIONS as Set-Cookie attributes, so refreshExpiry cannot
 * drift from the options the composable writes with: adding `secure` or `domain` to the
 * options above is picked up here instead of being silently dropped.
 */
const serializeCookieOptions = (options: Record<string, unknown>) =>
  Object.entries(options)
    .filter(([, value]) => value !== undefined && value !== false)
    .map(([key, value]) => {
      const attribute = key === "maxAge" ? "max-age" : key.toLowerCase();
      return value === true ? attribute : `${attribute}=${value}`;
    });

type TLocationParts = {
  lang: string;
  cinema: string;
};

export const useLastVisitedLocationCookie = () => {
  const clientCookie = useClientCookie(
    COOKIES_NAMES.lastVisitedLocation,
    LOCATION_COOKIE_OPTIONS,
  );

  const cookieValue = clientCookie.cookie;

  const has = computed(() => {
    return isDefined(cookieValue.value);
  });

  const lang = computed(() => {
    if (isDefined(cookieValue.value)) {
      return cookieValue.value.split("/")[1];
    }
    return null;
  });

  const cinema = computed(() => {
    if (isDefined(cookieValue.value)) {
      return cookieValue.value.split("/")[2];
    }
    return null;
  });

  const setValue = ({ lang, cinema }: TLocationParts) => {
    const cinemaSlug = isValidCinemaSlug(cinema)
      ? cinema
      : DEFAULT_CINEMA_HANDLER;

    const cinemaLang = isValidCinemaLang(lang) ? lang : DEFAULT_CINEMA_LANGUAGE;

    const newValue = `/${cinemaLang}/${cinemaSlug}`;

    // Set cookie only if value has changed (client-side only)
    if (cookieValue.value !== newValue) {
      clientCookie.setCookie(newValue);
    }
  };

  const setRawValue = (value: string) => {
    clientCookie.setCookie(value);
  };

  const clearValue = () => {
    clientCookie.deleteCookie();
  };

  /**
   * Re-serialize the current value with a fresh maxAge, so the expiry slides on every
   * visit instead of being fixed to the last cinema change. Written directly rather
   * than through setCookie: setValue only writes when the value differs, and assigning
   * an identical value to the ref triggers neither the Vue watcher nor Nuxt's own
   * isEqual-guarded write, so the Set-Cookie header would never be re-emitted.
   *
   * The normalized path is written back rather than the raw value, so a non-canonical
   * value (setRawValue stores a CMS nodeUri verbatim) is canonicalized instead of being
   * pinned for a year in whatever shape it happened to have.
   *
   * A value that no longer normalizes (removed/renamed cinema slug) is deleted, not
   * merely left unrefreshed: `has` would otherwise stay true for up to a year, so the
   * cinema picker would not open while both redirect layers ignore the value — silently
   * parking the user on the default cinema. As a session cookie this healed on browser
   * close; dropping it here restores that, immediately.
   *
   * Presence is tested with isDefined, not truthiness, so that "" and 0 (destr turns the
   * raw "0" into a number) take the delete branch rather than slipping past it: `has` is
   * isDefined-based too, so a falsy-but-present value would keep the picker shut while
   * both redirect layers ignore it.
   */
  const refreshExpiry = () => {
    if (import.meta.server || typeof document === "undefined") return;

    // A legacy domain-scoped cookie of the same name shadows the host-only one: cookie
    // parsing keeps the FIRST occurrence and the browser sends the older (domain-scoped)
    // cookie first. Re-persisting that shadowed value would overwrite the user's actual
    // choice for a year, and deleting would drop the host-only cookie while the legacy one
    // survives — so do neither and leave the duplicate to cleanupOldCookies.client.ts.
    if (countRawCookies(COOKIES_NAMES.lastVisitedLocation) > 1) return;

    const current = cookieValue.value;
    if (!isDefined(current)) return;

    const normalized = normalizeCinemaCookiePath(String(current));

    if (!normalized) {
      clientCookie.deleteCookie();
      return;
    }

    document.cookie = [
      `${COOKIES_NAMES.lastVisitedLocation}=${encodeURIComponent(normalized)}`,
      ...serializeCookieOptions(LOCATION_COOKIE_OPTIONS),
    ].join("; ");
  };

  return {
    cookie: readonly(cookieValue),
    lang,
    cinema,
    has,
    setValue,
    setRawValue,
    clearValue,
    refreshExpiry,
  };
};
